import { useEffect, useState } from 'react';
import { Card }     from 'primereact/card';
import { Tag }      from 'primereact/tag';
import { Button }   from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';
import { Chart }    from 'primereact/chart';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { Sidebar }  from 'primereact/sidebar';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column }   from 'primereact/column';
import { Message }  from 'primereact/message';

import { getEncuestasDashboard, getAnalisisIA, getTransporteEstatus } from './Integracion';
import useGetDataUser from "../../../utilidades/customHooks/useGetDataUser";
import {InputIcon} from "primereact/inputicon";
import {IconField} from "primereact/iconfield";

const MESES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
const PREGUNTAS = [
    ['puntualidad', 'Puntualidad'],
    ['limpieza',    'Limpieza'],
    ['operador',    'Operador'],
    ['manejo',      'Manejo'],
    ['trato',       'Trato'],
];
const MIN_MUESTRA = 10; // un mes con 8 encuestas en 5.00 no debería ser "mejor mes"
const C = { calif: '#6366f1', volumen: '#cbd5e1', resp: '#f59e0b', gris: '#64748b', bajo: '#ef4444', medio: '#f97316' };

// Colorea cada estatus de forma consistente
const ESTATUS_COLOR = {
    'Cotizado':   { bg: '#eff6ff', color: '#3b82f6', icon: 'pi-file'        },
    'Vendido':    { bg: '#f0fdf4', color: '#22c55e', icon: 'pi-check-circle' },
    'Consultado': { bg: '#fefce8', color: '#eab308', icon: 'pi-eye'          },
};
const defaultColor = { bg: '#f3f4f6', color: '#6b7280', icon: 'pi-tag' };

// ─── helpers ──────────────────────────────────────────────────────────────────
const iso        = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const finDeMes   = d => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const pct        = (a, b) => (b ? (a / b) * 100 : null);
const fmt        = (v, dec = 2) => (v == null ? '—' : v.toFixed(dec));
const fmtPct     = v => (v == null ? '—' : `${v.toFixed(1)}%`);
const fechaCorta = s => s.slice(0, 10).split('-').reverse().join('/');
const colorTexto = v => (v == null ? '' : v < 4.5 ? 'text-red-500' : v < 4.8 ? 'text-orange-500' : 'text-green-600');
const colorBarra = v => (v < 4.5 ? C.bajo : v < 4.8 ? C.medio : C.calif);
const califCell  = campo => row => <span className={`font-semibold ${colorTexto(row[campo])}`}>{fmt(row[campo])}</span>;

// Regresión lineal simple; ignora meses sin dato
const tendencia = valores => {
    const pts = valores.map((y, x) => [x, y]).filter(([, y]) => y != null);
    const n = pts.length;
    if (n < 2) return { linea: valores.map(() => null), pendiente: 0 };
    let sx = 0, sy = 0, sxy = 0, sxx = 0;
    pts.forEach(([x, y]) => { sx += x; sy += y; sxy += x * y; sxx += x * x; });
    const m = (n * sxy - sx * sy) / (n * sxx - sx * sx);
    const b = (sy - m * sx) / n;
    return { linea: valores.map((_, x) => b + m * x), pendiente: m };
};

const notaTendencia = (m, esPct) => ({
    texto: `${m >= 0 ? '▲' : '▼'} ${Math.abs(m).toFixed(esPct ? 1 : 3)}${esPct ? ' pts' : ''} por mes`,
    clase: m >= 0 ? 'text-green-600' : 'text-red-500',
});

// Dibuja el valor de cada punto/barra. Sustituye a chartjs-plugin-datalabels (sin instalar nada).
// Se activa por dataset con la propiedad `etiqueta: v => texto`.
const etiquetas = {
    id: 'etiquetas',
    afterDatasetsDraw: chart => {
        const { ctx } = chart;
        const horizontal = chart.options.indexAxis === 'y';
        chart.data.datasets.forEach((ds, i) => {
            if (!ds.etiqueta || !chart.isDatasetVisible(i)) return;
            chart.getDatasetMeta(i).data.forEach((el, j) => {
                const v = ds.data[j];
                if (v == null || v === 0) return;
                const { x, y } = el.tooltipPosition();
                ctx.save();
                ctx.font = '600 10px sans-serif';
                ctx.textAlign = horizontal ? 'right' : 'center';
                ctx.fillStyle = horizontal ? '#fff' : (ds.etiquetaColor ?? ds.borderColor);
                ctx.fillText(ds.etiqueta(v), horizontal ? x - 6 : x, horizontal ? y + 4 : y - 8);
                ctx.restore();
            });
        });
    },
};

const baseOpciones = {
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    layout: { padding: { top: 16 } },
    plugins: { legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } } } },
    scales: { x: { grid: { display: false }, ticks: { font: { size: 10 } } } },
};

// Mismos filtros para la carga del dashboard y para el análisis IA
const armarFiltros = (rango, chofer, propios) => {
    const [ini, fin] = rango ?? [];
    if (!ini || !fin) return null; // el usuario aún está eligiendo el rango
    return { desde: iso(ini), hasta: iso(finDeMes(fin)), propios: propios ? 1 : 0, ...(chofer && { chofer }) };
};

// Todo lo que se calcula a partir de la respuesta del backend
const armarVista = ({ resumen, mensual }) => {
    const filas = mensual.map(m => ({
        ...m,
        periodo: `${MESES[m.mes - 1]} ${String(m.anio).slice(2)}`,
        resp:  pct(m.encuestas, m.servicios),
        recom: pct(m.recomiendan, m.encuestas),
    }));
    const conMuestra = filas.filter(f => f.encuestas >= MIN_MUESTRA);
    const mejor = conMuestra.reduce((a, f) => (!a || f.total > a.total ? f : a), null);
    const peor  = conMuestra.reduce((a, f) => (!a || f.total < a.total ? f : a), null);
    const calif = filas.map(f => f.total);
    const resp  = filas.map(f => f.resp);
    const tCalif = tendencia(calif);
    const tResp  = tendencia(resp);

    return {
        filas, calif, resp, tCalif, tResp, mejor,
        labels: filas.map(f => f.periodo),
        porPregunta: PREGUNTAS.map(([k]) => resumen[k]),
        // Solo para las etiquetas del panel de IA (el análisis lo arma el backend)
        stats: {
            trend:      tCalif.pendiente >= 0 ? 'alza' : 'baja',
            bestMonth:  mejor?.periodo ?? '—',
            worstMonth: peor?.periodo ?? '—',
        },
    };
};

// ─── EstatusCards ─────────────────────────────────────────────────────────────
const EstatusCards = ({ items }) => {
    const grupos = items.reduce((acc, row) => {
        const key = row.ESTATUS ?? 'Sin estatus';
        acc[key] = (acc[key] ?? 0) + 1;
        return acc;
    }, {});

    return (
        <div className="flex flex-wrap gap-2">
            {Object.entries(grupos).map(([estatus, count]) => {
                const { bg, color, icon } = ESTATUS_COLOR[estatus] ?? defaultColor;
                return (
                    <div key={estatus} className="flex align-items-center gap-2 px-3 py-1 border-round-lg"
                         style={{ background: bg, border: `1px solid ${color}22` }}>
                        <i className={`pi ${icon}`} style={{ fontSize: '0.78rem', color }} />
                        <span style={{ fontSize: '0.7rem', color: '#6b7280', whiteSpace: 'nowrap' }}>{estatus}</span>
                        <span style={{ fontSize: '0.95rem', fontWeight: 600, color }}>{count}</span>
                    </div>
                );
            })}
        </div>
    );
};

// ─── AIPanel ──────────────────────────────────────────────────────────────────
// analysis vive en el padre para que no se pierda al cerrar el Sidebar
const AIPanel = ({ filtros, stats, analysis, setAnalysis }) => {
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState('');
    const user = useGetDataUser();

    const analizar = async () => {
        setLoading(true);
        setError('');
        try {
            const r = await getAnalisisIA(filtros, user?.user.ID);
            setAnalysis(r?.analisis ?? '');
        } catch {
            setError('No se pudo generar el análisis. Intenta de nuevo en unos minutos.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Button label={analysis ? 'Actualizar' : 'Analizar'} icon="pi pi-sparkles" size="small"
                    outlined loading={loading} onClick={analizar} className="mb-3" />

            {error && <Message severity="error" text={error} className="w-full mb-3" />}

            {loading ? (
                <div className="flex flex-column gap-2">
                    {[...Array(5)].map((_, i) => <Skeleton key={i} height="1rem" width={i % 2 ? '85%' : '100%'} />)}
                </div>
            ) : analysis ? (
                <>
                    <div className="flex flex-wrap gap-2 mb-3">
                        <Tag value={`Mejor: ${stats.bestMonth}`}  severity="success" />
                        <Tag value={`Menor: ${stats.worstMonth}`} severity="warning" />
                        <Tag value={`Tendencia: ${stats.trend}`} severity={stats.trend === 'alza' ? 'success' : 'danger'} />
                    </div>
                    <div style={{ fontSize: '0.85rem', lineHeight: 1.7, color: 'var(--text-color-secondary)' }}
                         dangerouslySetInnerHTML={{ __html: analysis }} />
                </>
            ) : (
                <p className="text-sm text-color-secondary">
                    Presiona Analizar para generar el análisis del periodo y filtros actuales.
                </p>
            )}
        </div>
    );
};

// ─── EncuestasDashboard ───────────────────────────────────────────────────────
const EncuestasDashboard = () => {
    const hoy = new Date();
    // Por defecto: últimos 12 meses completos (el mes en curso jala la tendencia a 0)
    const [rango, setRango] = useState([
        new Date(hoy.getFullYear(), hoy.getMonth() - 12, 1),
        new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1),
    ]);
    const [chofer,  setChofer]  = useState(null);
    const [propios, setPropios] = useState(true);
    const [data,    setData]    = useState(null);
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState('');
    const [transportes, setTransportes] = useState([]);
    const [verIA,     setVerIA]     = useState(false);
    const [buscar,    setBuscar]    = useState('');
    const [soloBajas, setSoloBajas] = useState(false);
    const [analisis,  setAnalisis]  = useState('');

    useEffect(() => {
        getTransporteEstatus().then(r => setTransportes(r ?? []));
    }, []);

    useEffect(() => {
        const filtros = armarFiltros(rango, chofer, propios);
        if (!filtros) return;

        setLoading(true);
        setError('');
        getEncuestasDashboard(filtros)
            .then(r => { setData(r); setAnalisis(''); }) // otro periodo/filtro: el análisis anterior ya no aplica
            .catch(() => setError('No se pudieron cargar las encuestas. Revisa la conexión e intenta de nuevo.'))
            .finally(() => setLoading(false));
    }, [rango, chofer, propios]);

    const hayDatos = data?.resumen?.encuestas > 0;
    const v = hayDatos ? armarVista(data) : null;

    return (
        <div className="p-3 flex flex-column gap-3">
            <EstatusCards items={transportes} />

            {/* ── filtros ── */}
            <div className="surface-card border-round-lg shadow-1 p-3 flex flex-wrap align-items-end gap-3">
                <div className="flex flex-column gap-1">
                    <label htmlFor="rango" className="text-sm text-600">Periodo</label>
                    <Calendar inputId="rango" value={rango} onChange={e => setRango(e.value)}
                              selectionMode="range" view="month" dateFormat="M yy" readOnlyInput showIcon />
                </div>
                <div className="flex flex-column gap-1">
                    <label htmlFor="chofer" className="text-sm text-600">Chofer</label>
                    <Dropdown inputId="chofer" value={chofer} onChange={e => setChofer(e.value)}
                              options={data?.choferes ?? []} optionLabel="nombre" optionValue="id"
                              placeholder="Todos" filter showClear className="w-full md:w-16rem" />
                </div>
                <div className="flex align-items-center gap-2 pb-2">
                    <InputSwitch inputId="propios" checked={propios} onChange={e => setPropios(e.value)} />
                    <label htmlFor="propios" className="text-sm">Solo servicios propios</label>
                </div>
                <Button label="Análisis IA" icon="pi pi-sparkles" outlined className="md:ml-auto"
                        onClick={() => setVerIA(true)} disabled={!hayDatos} />
            </div>

            {error && <Message severity="error" text={error} />}

            {!data && loading && <Skeleton height="20rem" />}

            {data && !hayDatos && (
                <Message severity="info" className="w-full"
                         text="No hay encuestas contestadas en este periodo. Amplía el rango o quita el filtro de chofer." />
            )}

            {hayDatos && (
                <div className={`flex flex-column gap-3 ${loading ? 'opacity-60' : ''}`}>
                    {/* ── KPIs ── */}
                    <div className="surface-card border-round-lg shadow-1 flex flex-wrap">
                        {[
                            { icon: 'pi-inbox', label: 'Encuestas', valor: data.resumen.encuestas.toLocaleString('es-MX'),
                                nota: `de ${data.resumen.servicios.toLocaleString('es-MX')} servicios` },
                            { icon: 'pi-percentage', label: 'Respuesta', valor: fmtPct(pct(data.resumen.encuestas, data.resumen.servicios)),
                                ...((n) => ({ nota: n.texto, clase: n.clase }))(notaTendencia(v.tResp.pendiente, true)) },
                            { icon: 'pi-star', label: 'Calificación', valor: fmt(data.resumen.total),
                                ...((n) => ({ nota: n.texto, clase: n.clase }))(notaTendencia(v.tCalif.pendiente)) },
                            { icon: 'pi-thumbs-up', label: 'Volverían a contratar', valor: fmtPct(pct(data.resumen.recomiendan, data.resumen.encuestas)) },
                            { icon: 'pi-exclamation-triangle', label: 'Calificaciones bajas', valor: data.resumen.bajas,
                                nota: 'menores a 4.5', clase: data.resumen.bajas ? 'text-red-500' : undefined },
                            { icon: 'pi-calendar', label: 'Mejor mes', valor: v.mejor?.periodo ?? '—',
                                nota: v.mejor ? `${fmt(v.mejor.total)} con ${v.mejor.encuestas} encuestas` : `ningún mes con ${MIN_MUESTRA}+` },
                        ].map(k => (
                            <div key={k.label} className="flex-1 p-3" style={{ minWidth: '10rem' }}>
                                <div className="flex align-items-center gap-2 text-600 text-sm">
                                    <i className={`pi ${k.icon}`} aria-hidden="true" /> {k.label}
                                </div>
                                <div className="text-2xl font-semibold mt-2">{k.valor}</div>
                                {k.nota && <div className={`text-xs mt-1 ${k.clase ?? 'text-500'}`}>{k.nota}</div>}
                            </div>
                        ))}
                    </div>

                    <div className="grid">
                        {/* ── calificación ── */}
                        <div className="col-12 lg:col-6">
                            <Card title="Calificación promedio por mes" className="h-full">
                                <Chart type="line" plugins={[etiquetas]} style={{ height: '260px' }}
                                       data={{
                                           labels: v.labels,
                                           datasets: [
                                               { label: 'Calificación promedio', data: v.calif, borderColor: C.calif, backgroundColor: C.calif,
                                                   tension: 0.3, pointRadius: 3, etiqueta: x => x.toFixed(2) },
                                               { label: 'Tendencia', data: v.tCalif.linea, borderColor: C.gris, borderDash: [6, 4],
                                                   borderWidth: 1.5, pointRadius: 0 },
                                           ],
                                       }}
                                       options={{
                                           ...baseOpciones,
                                           scales: {
                                               ...baseOpciones.scales,
                                               y: { min: Math.floor((Math.min(...v.calif.filter(x => x != null)) - 0.1) * 10) / 10,
                                                   max: 5.1, ticks: { stepSize: 0.1, font: { size: 10 } } },
                                           },
                                       }} />
                            </Card>
                        </div>

                        {/* ── volumen y % respuesta ── */}
                        <div className="col-12 lg:col-6">
                            <Card title="Encuestas contestadas y % de respuesta" className="h-full">
                                <Chart type="bar" plugins={[etiquetas]} style={{ height: '260px' }}
                                       data={{
                                           labels: v.labels,
                                           datasets: [
                                               { type: 'bar', label: 'Encuestas', data: v.filas.map(f => f.encuestas),
                                                   backgroundColor: C.volumen, borderRadius: 4, yAxisID: 'y', order: 2,
                                                   etiqueta: x => x, etiquetaColor: C.gris },
                                               { type: 'line', label: '% Respuesta', data: v.resp, borderColor: C.resp, backgroundColor: C.resp,
                                                   tension: 0.3, pointRadius: 3, yAxisID: 'y1', order: 1, etiqueta: x => `${x.toFixed(0)}%` },
                                               { type: 'line', label: 'Tendencia % resp.', data: v.tResp.linea, borderColor: C.resp,
                                                   borderDash: [6, 4], borderWidth: 1.5, pointRadius: 0, yAxisID: 'y1', order: 0 },
                                           ],
                                       }}
                                       options={{
                                           ...baseOpciones,
                                           scales: {
                                               ...baseOpciones.scales,
                                               y:  { beginAtZero: true, grace: '15%', title: { display: true, text: 'Encuestas' } },
                                               y1: { position: 'right', beginAtZero: true, grace: '15%',
                                                   grid: { drawOnChartArea: false }, ticks: { callback: x => `${x}%` } },
                                           },
                                       }} />
                            </Card>
                        </div>

                        {/* ── por pregunta ── */}
                        <div className="col-12 lg:col-4">
                            <Card title="Calificación por pregunta" className="h-full">
                                <Chart type="bar" plugins={[etiquetas]} style={{ height: '240px' }}
                                       data={{
                                           labels: PREGUNTAS.map(([, n]) => n),
                                           datasets: [{ label: 'Promedio', data: v.porPregunta, borderRadius: 4,
                                               backgroundColor: v.porPregunta.map(colorBarra), etiqueta: x => x.toFixed(2) }],
                                       }}
                                       options={{
                                           ...baseOpciones,
                                           indexAxis: 'y',
                                           layout: {},
                                           plugins: { legend: { display: false } },
                                           scales: {
                                               x: { min: Math.floor((Math.min(...v.porPregunta.filter(x => x != null)) - 0.2) * 10) / 10, max: 5 },
                                               y: { grid: { display: false } },
                                           },
                                       }} />
                                <p className="text-xs text-500 mb-0 mt-3">Verde 4.8 o más, naranja 4.5 a 4.79, rojo menos de 4.5.</p>
                            </Card>
                        </div>

                        {/* ── tablas ── */}
                        <div className="col-12 lg:col-8">
                            <Card className="h-full">
                                <TabView>
                                    <TabPanel header="Por mes">
                                        <DataTable value={[...v.filas].reverse()} size="small" stripedRows scrollable>
                                            <Column field="periodo" header="Periodo" frozen footer="Total" />
                                            <Column field="servicios" header="Servicios" footer={data.resumen.servicios} />
                                            <Column field="encuestas" header="Encuestas" footer={data.resumen.encuestas} />
                                            <Column header="% Resp." body={r => fmtPct(r.resp)}
                                                    footer={fmtPct(pct(data.resumen.encuestas, data.resumen.servicios))} />
                                            <Column header="Calif." body={califCell('total')} footer={fmt(data.resumen.total)} />
                                            {PREGUNTAS.map(([k, n]) => (
                                                <Column key={k} header={n} body={califCell(k)} footer={fmt(data.resumen[k])} />
                                            ))}
                                            <Column header="Contratarían" body={r => fmtPct(r.recom)}
                                                    footer={fmtPct(pct(data.resumen.recomiendan, data.resumen.encuestas))} />
                                        </DataTable>
                                    </TabPanel>

                                    <TabPanel header="Por chofer">
                                        <DataTable value={data.choferes.filter(c => c.encuestas > 0)
                                            .map(c => ({ ...c, recom: pct(c.recomiendan, c.encuestas) }))}
                                                   size="small" stripedRows scrollable paginator rows={10}
                                                   sortField="encuestas" sortOrder={-1}>
                                            <Column field="nombre" header="Chofer" sortable />
                                            <Column field="encuestas" header="Encuestas" sortable />
                                            <Column field="total" header="Calif." body={califCell('total')} sortable />
                                            <Column field="bajas" header="Bajas" sortable
                                                    body={r => <span className={r.bajas ? 'text-red-500 font-semibold' : ''}>{r.bajas}</span>} />
                                            <Column field="recom" header="Contratarían" body={r => fmtPct(r.recom)} sortable />
                                        </DataTable>
                                    </TabPanel>

                                    <TabPanel header="Detalle">
                                        <div className="flex flex-wrap align-items-center gap-3 mb-3">
                                            <IconField iconPosition="left">
                                                <InputIcon className="pi pi-search"> </InputIcon>
                                                <InputText value={buscar} onChange={e => setBuscar(e.target.value)}
                                                           placeholder="Buscar chofer o comentario" aria-label="Buscar" />
                                            </IconField>
                                            <div className="flex align-items-center gap-2">
                                                <Checkbox inputId="bajas" checked={soloBajas} onChange={e => setSoloBajas(e.checked)} />
                                                <label htmlFor="bajas" className="text-sm">Solo calificaciones bajas</label>
                                            </div>
                                        </div>
                                        <DataTable value={soloBajas ? data.detalle.filter(d => d.total < 4.5) : data.detalle}
                                                   size="small" stripedRows scrollable paginator rows={10}
                                                   globalFilter={buscar} globalFilterFields={['chofer', 'comentarios']}
                                                   emptyMessage="Ninguna encuesta coincide con la búsqueda.">
                                            <Column field="fecha" header="Fecha" body={r => fechaCorta(r.fecha)} sortable />
                                            <Column field="folio" header="Folio" />
                                            <Column field="chofer" header="Chofer" />
                                            {PREGUNTAS.map(([k, n]) => <Column key={k} field={k} header={`${n.slice(0, 4)}.`} />)}
                                            <Column field="total" header="Total" body={califCell('total')} sortable />
                                            <Column header="Contrataría" align="center" body={r => (
                                                <i className={`pi ${r.recomienda ? 'pi-thumbs-up text-green-600' : 'pi-thumbs-down text-red-500'}`}
                                                   aria-label={r.recomienda ? 'Sí' : 'No'} />
                                            )} />
                                            <Column field="comentarios" header="Comentarios" style={{ minWidth: '16rem' }} />
                                        </DataTable>
                                    </TabPanel>
                                </TabView>
                            </Card>
                        </div>
                    </div>
                </div>
            )}

            <Sidebar visible={verIA} position="right" onHide={() => setVerIA(false)}
                     className="w-full md:w-30rem" header="Análisis IA">
                {v && <AIPanel filtros={armarFiltros(rango, chofer, propios)} stats={v.stats}
                               analysis={analisis} setAnalysis={setAnalisis} />}
            </Sidebar>
        </div>
    );
};

export default EncuestasDashboard;
